import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState, type FormEvent } from "react";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  ClipboardList,
  FileText,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  FORM_TEXT_MAX_LENGTH,
  FORM_TEXT_MIN_LENGTH,
  FormTextCounter,
} from "@/components/form-text-counter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SectionHeading } from "@/components/section-heading";
import { LiestalPropertyFlyer } from "@/components/liestal-property-flyer";
import { OffMarketBuyerInquiry } from "@/components/property-inquiry-dialog";
import { submitOffMarket } from "@/lib/off-market.functions";
import { filesToPayload } from "@/lib/file-upload";
import propertyImage from "@/assets/immobilien-altstadt.jpg";

export const Route = createFileRoute("/mehrfamilienhaus-verkaufen")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "Mehrfamilienhaus diskret verkaufen | Begner" },
      {
        name: "description",
        content:
          "Mehrfamilienhaus oder Renditeliegenschaft diskret verkaufen. Persönliche Off-Market-Vermittlung an ausgewählte Investoren. Jetzt vertraulich anfragen.",
      },
    ],
  }),
  component: OffMarketPage,
});

const situations = [
  "Erbengemeinschaften",
  "Scheidungen und Vermögensaufteilungen",
  "Nachfolgeregelungen",
  "Mehrere Eigentümer",
  "Portfoliobereinigungen",
  "Weitere Situationen, in denen ein diskreter Verkauf gewünscht ist",
];

const faqs = [
  {
    question: "Wie viel ist meine Immobilie aktuell wert?",
    answer:
      "Der aktuelle Wert hängt unter anderem von Lage, Wohnfläche, Grundstück, Baujahr, Zustand und vergleichbaren Verkäufen in der Umgebung ab. Eine erste Einschätzung kann anhand dieser Daten erfolgen. Für einen verbindlichen Marktwert empfiehlt sich eine professionelle Bewertung.",
  },
  {
    question: "Wie viel Miete kann ich für meine Immobilie verlangen?",
    answer:
      "Das hängt von Lage, Größe, Ausstattung, Zustand und den lokalen Mietpreisen ab. Vergleichbare Wohnungen und Häuser in der Umgebung geben einen guten ersten Anhaltspunkt.",
  },
  {
    question: "Soll ich meine Immobilie verkaufen oder behalten?",
    answer:
      "Das hängt von Ihrer finanziellen Situation, der erwarteten Wertentwicklung, der Mietrendite, den Finanzierungskosten und Ihren persönlichen Zielen ab. Eine Gegenüberstellung von Verkaufserlös und langfristigem Mietertrag kann bei der Entscheidung helfen.",
  },
  {
    question: "Lohnt sich meine Immobilie als Kapitalanlage?",
    answer:
      "Entscheidend sind Kaufpreis, Mieteinnahmen, laufende Kosten, Finanzierung, Steuern und mögliche Wertsteigerungen. Eine einfache Renditeberechnung zeigt, ob das Investment attraktiv ist.",
  },
  {
    question: "Wie hoch ist die Rendite meiner Immobilie?",
    answer:
      "Die Bruttorendite lässt sich vereinfacht berechnen: jährliche Mieteinnahmen geteilt durch den Kauf- bzw. Immobilienwert × 100. Für eine realistische Beurteilung sollten zusätzlich Unterhaltskosten, Finanzierung, Steuern und Leerstand berücksichtigt werden.",
  },
  {
    question: "Wie viel Cashflow bringt meine Immobilie?",
    answer:
      "Der Cashflow ergibt sich aus den Einnahmen abzüglich aller laufenden Ausgaben. Dazu gehören beispielsweise Hypothekarzinsen, Unterhalt, Versicherungen, Verwaltung, Nebenkosten und Steuern.",
  },
  {
    question: "Soll ich meine Hypothek amortisieren?",
    answer:
      "Eine Amortisation reduziert die Schulden und damit langfristig die Zinskosten. Ob sie sinnvoll ist, hängt unter anderem vom Zinssatz, der Steuerbelastung und den alternativen Anlagemöglichkeiten ab.",
  },
  {
    question: "Welche Renovationen erhöhen den Wert meiner Immobilie?",
    answer:
      "Besonders interessant können Küche, Badezimmer, energetische Verbesserungen, Heizung, Fenster und allgemeine Modernisierungen sein. Entscheidend ist aber, dass die Investition zum Wert und zur Lage der Immobilie passt.",
  },
  {
    question: "Was kostet eine Renovation?",
    answer:
      "Die Kosten hängen stark vom Umfang, den Materialien, der Wohnfläche und den lokalen Handwerkerpreisen ab. Für eine erste Schätzung kann man einzelne Arbeiten und Quadratmeterpreise miteinander vergleichen.",
  },
  {
    question: "Welche Renovationskosten kann ich steuerlich abziehen?",
    answer:
      "Das hängt vom Land und den jeweiligen kantonalen bzw. regionalen Regeln ab. In der Schweiz können bestimmte werterhaltende Kosten steuerlich anders behandelt werden als wertvermehrende Investitionen.",
  },
  {
    question: "Wie kann ich meine Immobilie schneller verkaufen?",
    answer:
      "Ein realistischer Verkaufspreis, professionelle Fotos, ein gutes Inserat und eine überzeugende Präsentation können die Nachfrage erhöhen. Auch kleine Reparaturen und eine gepflegte Immobilie können einen positiven Eindruck erzeugen.",
  },
  {
    question: "Wie schreibe ich ein gutes Immobilieninserat?",
    answer:
      "Ein gutes Inserat sollte die wichtigsten Vorteile der Immobilie schnell vermitteln: Lage, Wohnfläche, Zimmer, Ausstattung, Zustand, Besonderheiten und Preis. Hochwertige Fotos und eine klare Beschreibung sind besonders wichtig.",
  },
  {
    question: "Ist der Kaufpreis meiner Immobilie zu hoch?",
    answer:
      "Das lässt sich durch einen Vergleich mit ähnlichen Immobilien in derselben Region beurteilen. Zusätzlich sollten Zustand, Grundstück, Lage, Renovationsbedarf und mögliche zukünftige Kosten berücksichtigt werden.",
  },
  {
    question: "Wie viel Hypothek kann ich mir leisten?",
    answer:
      "Das hängt von Einkommen, Eigenkapital, laufenden Verpflichtungen und dem Wert der Immobilie ab. Banken prüfen zusätzlich, ob die Finanzierung auch bei höheren kalkulatorischen Zinsen tragbar bleibt.",
  },
  {
    question: "Welche Hypothek ist besser: Festhypothek oder SARON?",
    answer:
      "Das hängt von Ihrer Risikobereitschaft und Ihren finanziellen Zielen ab. Eine Festhypothek bietet mehr Planungssicherheit, während eine SARON-Hypothek stärker auf die Entwicklung der kurzfristigen Zinsen reagiert.",
  },
  {
    question: "Darf ich die Miete erhöhen?",
    answer:
      "Das hängt von der jeweiligen Gesetzgebung, dem Mietvertrag und dem Grund für die Erhöhung ab. Bei einer konkreten Situation sollte geprüft werden, welche gesetzlichen Vorgaben einzuhalten sind.",
  },
  {
    question: "Wie kann ich meine Nebenkostenabrechnung überprüfen?",
    answer:
      "Man sollte prüfen, welche Kosten im Mietvertrag vereinbart wurden, ob die einzelnen Positionen nachvollziehbar sind und ob die Kosten korrekt auf die Mieter verteilt wurden.",
  },
  {
    question: "Was kann ich tun, wenn mein Mieter nicht bezahlt?",
    answer:
      "Zunächst sollte die Situation schriftlich dokumentiert und der Mieter korrekt gemahnt werden. Das weitere Vorgehen richtet sich nach dem geltenden Mietrecht und sollte bei Streitfällen professionell abgeklärt werden.",
  },
  {
    question: "Lohnt sich eine Wärmepumpe oder Solaranlage?",
    answer:
      "Das hängt unter anderem vom Gebäude, Energieverbrauch, aktuellen Heizsystem, Investitionskosten, Förderungen und erwarteten Energieeinsparungen ab. Eine Wirtschaftlichkeitsberechnung kann zeigen, wann sich die Investition amortisiert.",
  },
  {
    question: "Was passiert steuerlich beim Verkauf meiner Immobilie?",
    answer:
      "Beim Verkauf können je nach Land und Region verschiedene Steuern anfallen. In der Schweiz ist insbesondere die Grundstückgewinnsteuer relevant. Die genaue Berechnung hängt unter anderem von Kaufpreis, Verkaufspreis, Besitzdauer und anerkannten Kosten ab.",
  },
  {
    question: "Wie kann ich meine Immobilienrendite verbessern?",
    answer:
      "Mögliche Ansätze sind eine bessere Vermietung, die Reduzierung unnötiger Kosten, eine optimierte Finanzierung oder sinnvolle Renovationen. Wichtig ist, nicht nur die Einnahmen, sondern die gesamte Rendite zu betrachten.",
  },
  {
    question: "Wie finde ich einen guten Mieter?",
    answer:
      "Ein gutes Mietinserat, eine sorgfältige Prüfung der Bewerber und klare Auswahlkriterien helfen dabei. Wichtig sind unter anderem Zahlungsfähigkeit, Referenzen und ein zuverlässiger Eindruck.",
  },
  {
    question: "Wie verhindere ich Leerstand?",
    answer:
      "Ein marktgerechter Mietpreis, eine attraktive Präsentation und eine schnelle Reaktion auf Interessenten können helfen. Auch kleinere Verbesserungen an der Immobilie können die Vermietbarkeit erhöhen.",
  },
  {
    question: "Soll ich meine Immobilie renovieren oder direkt verkaufen?",
    answer:
      "Das hängt davon ab, wie stark die Renovation den Verkaufspreis erhöhen kann und welche Kosten dafür entstehen. Entscheidend ist die Differenz zwischen zusätzlichem Verkaufserlös und Renovationskosten.",
  },
  {
    question: "Wie kann ich meine Immobilie langfristig wertvoller machen?",
    answer:
      "Eine gute Instandhaltung, energetische Verbesserungen, zeitgemäße Ausstattung und die Pflege von Gebäude und Grundstück können langfristig zum Werterhalt beitragen. Wichtig ist, Investitionen sorgfältig nach Kosten und Nutzen zu beurteilen.",
  },
];

function Field({
  name,
  label,
  required = false,
  type = "text",
  ...props
}: { name: string; label: string; required?: boolean } & React.ComponentProps<typeof Input>) {
  return (
    <div className="space-y-2">
      <Label htmlFor={name}>
        {label}
        {required ? " *" : ""}
      </Label>
      <Input id={name} name={name} type={type} required={required} className="h-11" {...props} />
    </div>
  );
}

function OffMarketPage() {
  const submit = useServerFn(submitOffMarket);
  const [files, setFiles] = useState<File[]>([]);
  const [remarksLength, setRemarksLength] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState<{ reference: string; confirmationSent: boolean } | null>(
    null,
  );
  const successRef = useRef<HTMLDivElement>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const values = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    setError("");
    try {
      const result = await submit({
        data: { ...values, consent: values.consent === "on", files: await filesToPayload(files) },
      });
      setSuccess(result);
      requestAnimationFrame(() => {
        successRef.current?.focus();
        successRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    } catch {
      setError(
        "Ihre Anfrage konnte nicht gesendet werden. Bitte prüfen Sie Ihre Angaben und versuchen Sie es erneut. Ihre Eingaben bleiben erhalten.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main lang="de" className="[overflow-wrap:anywhere]">
      <section className="overflow-hidden border-b border-border bg-surface">
        <div className="container-page py-8 md:py-10 lg:py-12">
          <div className="grid items-center gap-8 lg:grid-cols-[1.15fr_.85fr]">
            <div>
              <SectionHeading
                level="page"
                eyebrow="Persönliche Off-Market-Vermittlung"
                title="Mehrfamilienhaus diskret verkaufen"
                headingClassName="text-3xl md:text-4xl"
                description="Sie möchten ein Mehrfamilienhaus oder eine Renditeliegenschaft verkaufen – diskret, ohne öffentliche Ausschreibung?"
                descriptionClassName="text-base leading-relaxed"
              />
              <p className="mt-4 leading-relaxed text-muted-foreground">
                begner.ch verfügt über ein ausgewähltes Netzwerk von Investoren und begleitet
                Eigentümer bei diskreten Off-Market-Verkäufen professionell, persönlich und
                zielgerichtet.
              </p>
              <Button asChild size="lg" className="mt-6 rounded-xl font-semibold shadow-brand">
                <a href="#anfrage">
                  Vertraulich anfragen <ArrowRight className="size-4" />
                </a>
              </Button>
            </div>
            <div className="relative">
              <img
                src={propertyImage}
                alt="Wohnliegenschaften in einem Schweizer Altstadtquartier"
                className="aspect-[16/10] w-full rounded-2xl border border-border object-cover shadow-card"
              />
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <ShieldCheck className="size-4 shrink-0 text-brand-deep" />
                Ihre Liegenschaft wird nicht öffentlich inseriert.
              </div>
            </div>
          </div>
          <div className="mt-8 grid gap-4 border-t border-border pt-5 sm:grid-cols-3">
            {[
              [ShieldCheck, "Vertrauliche Behandlung"],
              [Users, "Sorgfältige Käuferauswahl"],
              [CheckCircle2, "Persönliche Begleitung"],
            ].map(([Icon, text]) => {
              const TrustIcon = Icon as typeof ShieldCheck;
              return (
                <div key={String(text)} className="flex items-center gap-3 text-sm font-semibold">
                  <TrustIcon className="size-5 text-brand-deep" />
                  {String(text)}
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="bg-background py-12 md:py-16">
        <div className="container-page">
          <SectionHeading
            eyebrow="So funktioniert es"
            title="In drei Schritten zum passenden Käufer"
            className="max-w-2xl"
          />
          <div className="mt-7 grid gap-4 md:grid-cols-3">
            {[
              {
                icon: ClipboardList,
                title: "Eckdaten übermitteln",
                text: "Teilen Sie uns die wichtigsten Angaben zu Ihrer Liegenschaft mit. Vorhandene Unterlagen können Sie direkt geschützt hochladen.",
              },
              {
                icon: Users,
                title: "Persönlich besprechen",
                text: "Wir prüfen Ihre Angaben und besprechen mit Ihnen Ihre Vorstellungen, den Zeitrahmen und die nächsten Schritte.",
              },
              {
                icon: ShieldCheck,
                title: "Gezielt vermitteln",
                text: "Nach Rücksprache mit Ihnen stellen wir Ihre Liegenschaft passenden Investoren aus unserem Netzwerk vor. Objektangaben und Unterlagen geben wir erst nach Ihrer Zustimmung weiter.",
              },
            ].map(({ icon: Icon, title, text }, index) => (
              <div
                key={title}
                className="group relative rounded-2xl border border-border bg-card p-6 shadow-card transition duration-300 hover:-translate-y-1 hover:border-brand hover:shadow-brand"
              >
                <div className="absolute -top-4 left-6 rounded-full bg-gradient-brand px-3 py-1 text-xs font-bold text-primary-foreground shadow-brand">
                  Schritt {index + 1}
                </div>
                <div className="flex size-12 items-center justify-center rounded-xl bg-brand-soft text-brand-deep transition group-hover:bg-brand group-hover:text-primary-foreground">
                  <Icon className="size-6" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold transition-colors group-hover:text-brand-deep">
                  {title}
                </h3>
                <p className="mt-3 text-muted-foreground">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="border-y border-border bg-surface py-12 md:py-16">
        <div className="container-page grid gap-8 lg:grid-cols-2">
          <SectionHeading
            eyebrow="Ihre Situation im Blick"
            title="Auch bei komplexen Eigentumssituationen"
            headingClassName="text-2xl md:text-3xl"
            description="Wir legen besonderen Wert auf Vertraulichkeit, eine sorgfältige Käuferauswahl und eine effiziente Abwicklung. Unsere diskrete Vermittlung eignet sich insbesondere für:"
          />
          <ul className="space-y-3">
            {situations.map((text) => (
              <li key={text} className="flex gap-3">
                <Check className="mt-0.5 size-5 shrink-0 text-brand-deep" />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <section id="anfrage" className="scroll-mt-24 py-12 md:py-16">
        <div className="container-page max-w-4xl">
          <SectionHeading
            eyebrow="Sie suchen einen Käufer?"
            title="Ihre Liegenschaft vertraulich anbieten"
            headingClassName="text-2xl md:text-3xl"
            description="Teilen Sie uns die wichtigsten Eckdaten mit. Wir prüfen Ihre Angaben und bringen Ihr Objekt gezielt mit passenden Investoren zusammen."
          />
          {success ? (
            <div
              ref={successRef}
              tabIndex={-1}
              role="status"
              className="mt-7 rounded-2xl border border-brand/20 bg-brand-soft p-6"
            >
              <CheckCircle2 className="size-8 text-brand-deep" />
              <h3 className="mt-4 font-display text-2xl font-bold">
                Vielen Dank für Ihr Vertrauen.
              </h3>
              <p className="mt-3">
                Ihre Anfrage ist sicher eingegangen. Wir melden uns persönlich bei Ihnen.
              </p>
              <p className="mt-3 text-sm">Referenz: {success.reference}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                {success.confirmationSent
                  ? "Sie erhalten eine Eingangsbestätigung per E-Mail."
                  : "Die E-Mail-Bestätigung konnte nicht versendet werden. Ihre Anfrage ist gespeichert."}
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="mt-7 space-y-6 rounded-2xl border border-border bg-card p-5 shadow-card md:p-6"
            >
              <p className="text-sm text-muted-foreground">
                Mit * markierte Felder sind Pflichtfelder. Alle weiteren Angaben sind freiwillig.
              </p>
              <fieldset disabled={busy} className="space-y-5 disabled:opacity-60">
                <legend className="mb-5 font-display text-lg font-bold">Ihre Liegenschaft</legend>
                <Field
                  name="street"
                  label="Strasse und Hausnummer"
                  required
                  maxLength={180}
                  autoComplete="street-address"
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    name="postalCode"
                    label="PLZ"
                    required
                    pattern="[0-9]{4}"
                    inputMode="numeric"
                    maxLength={4}
                    autoComplete="postal-code"
                  />
                  <Field
                    name="city"
                    label="Ort"
                    required
                    maxLength={100}
                    autoComplete="address-level2"
                  />
                  <Field
                    name="apartments"
                    label="Anzahl Wohnungen"
                    required
                    type="number"
                    min={1}
                    max={10000}
                    step={1}
                  />
                  <Field
                    name="year"
                    label="Baujahr"
                    type="number"
                    min={1000}
                    max={new Date().getFullYear()}
                    step={1}
                  />
                  <Field
                    name="land"
                    label="Grundstücksfläche (m²)"
                    type="number"
                    min={1}
                    step="any"
                  />
                  <Field
                    name="income"
                    label="Jährlicher Nettomietertrag (CHF)"
                    type="number"
                    min={0}
                    step="any"
                  />
                  {[
                    {
                      name: "occupancy",
                      label: "Vermietungsstand",
                      options: ["Voll vermietet", "Teilweise vermietet", "Leer"],
                    },
                    {
                      name: "condition",
                      label: "Zustand",
                      options: ["Renoviert", "Teilweise renoviert", "Renovationsbedürftig"],
                    },
                  ].map((field) => (
                    <div key={field.name} className="space-y-2">
                      <Label htmlFor={field.name}>{field.label}</Label>
                      <Select name={field.name} defaultValue="">
                        <SelectTrigger id={field.name} className="h-11 w-full">
                          <SelectValue placeholder="Bitte wählen (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <Field
                  name="price"
                  label="Ungefährer gewünschter Verkaufspreis (CHF), falls bekannt"
                  type="number"
                  min={1}
                  step="any"
                />
              </fieldset>
              <fieldset
                disabled={busy}
                className="space-y-5 border-t border-border pt-5 disabled:opacity-60"
              >
                <legend className="pr-3 font-display text-lg font-bold">Kontakt und Termin</legend>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    name="firstName"
                    label="Vorname"
                    required
                    maxLength={55}
                    autoComplete="given-name"
                  />
                  <Field
                    name="lastName"
                    label="Nachname"
                    required
                    maxLength={55}
                    autoComplete="family-name"
                  />
                  <Field
                    name="phone"
                    label="Telefonnummer"
                    required
                    type="tel"
                    minLength={6}
                    maxLength={40}
                    autoComplete="tel"
                  />
                  <Field
                    name="email"
                    label="E-Mail"
                    required
                    type="email"
                    maxLength={255}
                    autoComplete="email"
                  />
                </div>
                <Field
                  name="callback"
                  label="Wann dürfen wir Sie anrufen?"
                  placeholder="Zum Beispiel werktags zwischen 17 und 19 Uhr"
                  maxLength={200}
                />
                <Field
                  name="viewing"
                  label="Unverbindlichen Besichtigungstermin vorschlagen"
                  placeholder="Gewünschtes Datum und Uhrzeit"
                  maxLength={200}
                />
                <p className="text-sm text-muted-foreground">
                  Wir stimmen den Termin persönlich mit Ihnen ab.
                </p>
              </fieldset>
              <fieldset
                disabled={busy}
                className="space-y-5 border-t border-border pt-5 disabled:opacity-60"
              >
                <legend className="pr-3 font-display text-lg font-bold">
                  Unterlagen und Bemerkungen
                </legend>
                <div className="space-y-2">
                  <Label htmlFor="documents">Fotos, Mieterspiegel oder Verkaufsunterlagen</Label>
                  <Input
                    id="documents"
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    aria-describedby="upload-help"
                    onChange={(event) => {
                      const next = [...files, ...Array.from(event.target.files ?? [])];
                      event.target.value = "";
                      if (
                        next.length > 5 ||
                        next.some(
                          (file) =>
                            file.size > 4 * 1024 * 1024 ||
                            !["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(
                              file.type,
                            ),
                        ) ||
                        next.reduce((sum, file) => sum + file.size, 0) > 12 * 1024 * 1024
                      ) {
                        setError(
                          "Bitte maximal 5 Dateien als PDF, JPG, PNG oder WebP wählen: je 4 MB, insgesamt 12 MB.",
                        );
                        return;
                      }
                      setError("");
                      setFiles(next);
                    }}
                  />
                  <p id="upload-help" className="text-sm text-muted-foreground">
                    PDF, JPG, PNG oder WebP. Maximal 5 Dateien, je 4 MB, insgesamt 12 MB. Ihre
                    Unterlagen sind nur für Begner zugänglich.
                  </p>
                  {files.length > 0 && (
                    <ul className="space-y-2 pt-2">
                      {files.map((file, index) => (
                        <li
                          key={`${file.name}-${index}`}
                          className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm"
                        >
                          <FileText className="size-4 shrink-0" />
                          <span className="min-w-0 flex-1 truncate">{file.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`${file.name} entfernen`}
                            onClick={() => setFiles(files.filter((_, i) => i !== index))}
                          >
                            <X className="size-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="remarks">Bemerkungen</Label>
                  <Textarea
                    id="remarks"
                    name="remarks"
                    rows={4}
                    minLength={FORM_TEXT_MIN_LENGTH}
                    maxLength={FORM_TEXT_MAX_LENGTH}
                    aria-describedby="remarks-count"
                    placeholder="Was möchten Sie uns noch mitteilen?"
                    onChange={(event) => setRemarksLength(event.target.value.length)}
                  />
                  <FormTextCounter length={remarksLength} language="de" id="remarks-count" />
                </div>
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input id="website" name="website" tabIndex={-1} autoComplete="off" />
                </div>
                <label className="flex items-start gap-3 text-sm leading-relaxed">
                  <input
                    type="checkbox"
                    name="consent"
                    required
                    className="mt-1 size-4 shrink-0 accent-primary"
                  />
                  <span>
                    Ich habe die{" "}
                    <Link
                      to="/datenschutz"
                      className="font-medium text-brand-deep underline underline-offset-4"
                    >
                      Datenschutzerklärung
                    </Link>{" "}
                    gelesen und bin mit der Verarbeitung meiner Angaben zur Bearbeitung dieser
                    vertraulichen Anfrage einverstanden. *
                  </span>
                </label>
              </fieldset>
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                size="lg"
                disabled={busy}
                className="h-auto min-h-11 w-full whitespace-normal rounded-xl font-semibold shadow-brand sm:w-auto"
              >
                <ShieldCheck className="size-4 shrink-0" />
                {busy ? "Anfrage wird gesendet …" : "Vertrauliche Anfrage senden"}
              </Button>
            </form>
          )}
        </div>
      </section>
      <section className="container-page space-y-6 pb-12 md:pb-16">
        <OffMarketBuyerInquiry language="de" />
        <LiestalPropertyFlyer language="de" />
      </section>
      <section className="container-page pb-14 sm:pb-20">
        <div className="mx-auto max-w-3xl">
          <p className="text-center text-xs font-bold uppercase tracking-[0.18em] text-brand-deep">
            Gut zu wissen
          </p>
          <h2 className="mt-3 text-center font-display text-3xl font-bold tracking-tight">
            Häufige Fragen rund um Ihre Immobilie
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center leading-7 text-muted-foreground">
            Antworten auf wichtige Fragen zu Bewertung, Rendite, Vermietung, Renovation und Verkauf.
          </p>
          <Accordion
            type="single"
            collapsible
            className="mt-8 rounded-2xl border border-border bg-card px-5 sm:px-7"
          >
            {faqs.map((faq, index) => (
              <AccordionItem key={faq.question} value={`property-faq-${index}`}>
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="max-w-2xl text-sm leading-6 text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </main>
  );
}
